/**
 * Purpose-centered IA remount — Primary 1 Family B.
 *
 * Consumes deriveDomainNavigation builders / domainRouteConfig leaves.
 * Does not invent a parallel path tree.
 */

import React from 'react';
import {
  ADVANCED_NAVIGATION_LAYERS_V0,
  PRIMARY_NAVIGATION_AREAS_V0,
} from '@shared/frontend-experience-architecture-and-navigation-canon';
import {
  automationSubRoutes,
  exportSubRoutes,
  physicalAccessSubRoutes,
  systemSubRoutes,
  workspaceSubRoutes,
} from './domainRouteConfig';
import { buildAbsolutePath, resolveRouteLabel } from './domainRouteConfigSchema';
import type { AppMenuItem } from './appMenuConfig'; // type-only — avoid runtime cycle
import {
  buildAppealNavMenuItem,
  buildCalendarNavMenuItem,
  buildConsciousFooterMenuItem,
  buildEntityNavMenuItem,
  buildFinanceAdvancedDomainMenuItem,
  buildFinanceDomainMenuItem,
  buildFlowFooterMenuItem,
  buildItemNavMenuItem,
  buildKnowledgeNavMenuItem,
  buildLogNavMenuItem,
  buildManufacturingNavMenuItem,
  buildSpaceNavMenuItem,
  buildSystemFooterMenuItem,
} from './deriveDomainNavigation';

function leaf(key: string, path: string, label: string, icon: React.ReactNode = null): AppMenuItem {
  return { key, path, label, icon };
}

function workspaceLeafById(id: string): AppMenuItem | null {
  const route = workspaceSubRoutes.find((entry) => entry.id === id);
  if (!route) return null;
  return leaf(route.id ?? id, buildAbsolutePath('workspace', route.path), resolveRouteLabel(route));
}

function systemLeafById(id: string): AppMenuItem | null {
  const route = systemSubRoutes.find((entry) => entry.id === id);
  if (!route || route.menuVisible === false) return null;
  return leaf(route.id ?? id, buildAbsolutePath('system', route.path), resolveRouteLabel(route));
}

/** Approvals + document-review — Evidence & Decisions area (removed from Flow daily). */
export function buildEvidenceDecisionsAreaChildren(): AppMenuItem[] {
  return [
    leaf('flow-approvals-pending', '/approvals/pending', '승인 대기'),
    leaf('flow-approvals-history', '/approvals/history', '승인 이력'),
    systemLeafById('system-document-review-inbox'),
    systemLeafById('system-document-review-new'),
    systemLeafById('system-document-review-support'),
  ].filter((item): item is AppMenuItem => item !== null);
}

export function buildTodayAreaChildren(icons: { physicalAccess?: React.ReactNode }): AppMenuItem[] {
  const hub = workspaceLeafById('workspace-hub');
  const assignments = workspaceLeafById('workspace-assignments');
  const physical = physicalAccessSubRoutes
    .filter((route) => route.menuVisible !== false)
    .map((route) =>
      leaf(
        route.id ?? `physical-access-${route.path}`,
        buildAbsolutePath('physical-access', route.path),
        resolveRouteLabel(route),
        icons.physicalAccess ?? null
      )
    );
  return [hub, assignments, ...physical].filter((item): item is AppMenuItem => item !== null);
}

export function buildWorkbenchAreaChildren(): AppMenuItem[] {
  // Single governed-execution entry — avoid duplicating flow-assignments (Operations owns it).
  const tasks = workspaceLeafById('workspace-tasks-index');
  return [tasks].filter((item): item is AppMenuItem => item !== null);
}

export function buildOperationsAreaChildren(icons: {
  manufacturing: React.ReactNode;
  finance: React.ReactNode;
  log: React.ReactNode;
}): AppMenuItem[] {
  const flow = buildFlowFooterMenuItem(null);
  const flowChildren = (flow.children ?? []).map((group) => {
    if (group.key !== 'flow-daily-group' || !group.children) return group;
    return {
      ...group,
      children: group.children.filter(
        (child) => child.key !== 'flow-approvals-pending' && child.key !== 'flow-approvals-history'
      ),
    };
  });
  // Keep domain parent key `flow` so RBAC visibility / warehouse path gates still match.
  return [
    { ...flow, children: flowChildren },
    buildManufacturingNavMenuItem(icons.manufacturing),
    buildFinanceDomainMenuItem(icons.finance),
    buildLogNavMenuItem(icons.log),
  ];
}

export function buildObjectsAreaChildren(icons: {
  item: React.ReactNode;
  space: React.ReactNode;
  knowledge: React.ReactNode;
  calendar: React.ReactNode;
  appeal: React.ReactNode;
}): AppMenuItem[] {
  return [
    buildItemNavMenuItem(icons.item),
    buildSpaceNavMenuItem(icons.space),
    buildKnowledgeNavMenuItem(icons.knowledge),
    buildCalendarNavMenuItem(icons.calendar),
    buildAppealNavMenuItem(icons.appeal),
  ];
}

export function buildPeopleAuthorityAreaChildren(icon: React.ReactNode): AppMenuItem[] {
  return [buildEntityNavMenuItem(icon)];
}

export function buildAutomationAreaChildren(icon: React.ReactNode): AppMenuItem[] {
  const automationRoute = automationSubRoutes[0];
  if (!automationRoute) {
    throw new Error('automationSubRoutes SSOT is empty');
  }
  return [
    leaf(
      'automation',
      buildAbsolutePath('automation', automationRoute.path),
      resolveRouteLabel(automationRoute),
      icon
    ),
  ];
}

export function buildPurposeCenteredDomainItems(icons: {
  today: React.ReactNode;
  workbench: React.ReactNode;
  operations: React.ReactNode;
  objects: React.ReactNode;
  evidence: React.ReactNode;
  people: React.ReactNode;
  automation: React.ReactNode;
  manufacturing: React.ReactNode;
  finance: React.ReactNode;
  log: React.ReactNode;
  item: React.ReactNode;
  space: React.ReactNode;
  knowledge: React.ReactNode;
  calendar: React.ReactNode;
  appeal: React.ReactNode;
}): AppMenuItem[] {
  const byId = Object.fromEntries(PRIMARY_NAVIGATION_AREAS_V0.map((a) => [a.areaId, a]));

  return [
    {
      key: byId.today!.navKey,
      path: byId.today!.defaultLanding,
      icon: icons.today,
      label: byId.today!.userFacingLabel,
      children: buildTodayAreaChildren({}),
    },
    {
      key: byId.workbench!.navKey,
      path: byId.workbench!.defaultLanding,
      icon: icons.workbench,
      label: byId.workbench!.userFacingLabel,
      children: buildWorkbenchAreaChildren(),
    },
    {
      key: byId.operations!.navKey,
      path: byId.operations!.defaultLanding,
      icon: icons.operations,
      label: byId.operations!.userFacingLabel,
      children: buildOperationsAreaChildren({
        manufacturing: icons.manufacturing,
        finance: icons.finance,
        log: icons.log,
      }),
    },
    {
      key: byId.objects!.navKey,
      path: byId.objects!.defaultLanding,
      icon: icons.objects,
      label: byId.objects!.userFacingLabel,
      children: buildObjectsAreaChildren({
        item: icons.item,
        space: icons.space,
        knowledge: icons.knowledge,
        calendar: icons.calendar,
        appeal: icons.appeal,
      }),
    },
    {
      key: byId.evidence_decisions!.navKey,
      path: byId.evidence_decisions!.defaultLanding,
      icon: icons.evidence,
      label: byId.evidence_decisions!.userFacingLabel,
      children: buildEvidenceDecisionsAreaChildren(),
    },
    {
      key: byId.people_authority!.navKey,
      path: byId.people_authority!.defaultLanding,
      icon: icons.people,
      label: byId.people_authority!.userFacingLabel,
      children: buildPeopleAuthorityAreaChildren(icons.people),
    },
    {
      key: byId.automation!.navKey,
      path: byId.automation!.defaultLanding,
      icon: icons.automation,
      label: byId.automation!.userFacingLabel,
      children: buildAutomationAreaChildren(icons.automation),
    },
  ];
}

export function buildPurposeCenteredFooterItems(icons: {
  configure: React.ReactNode;
  console: React.ReactNode;
}): AppMenuItem[] {
  const configure = ADVANCED_NAVIGATION_LAYERS_V0.find((a) => a.areaId === 'configure')!;
  const consoleLayer = ADVANCED_NAVIGATION_LAYERS_V0.find(
    (a) => a.areaId === 'operations_console'
  )!;

  const system = buildSystemFooterMenuItem(icons.configure);
  const conscious = buildConsciousFooterMenuItem(icons.console);

  // Document-review leaves moved to Evidence area — hide duplicates from Configure.
  const configureChildren = (system.children ?? []).filter(
    (child) =>
      child.key !== 'system-document-review-inbox' &&
      child.key !== 'system-document-review-new' &&
      child.key !== 'system-document-review-support'
  );

  const exportRoute = exportSubRoutes[0];
  if (exportRoute) {
    configureChildren.push(
      leaf('export', buildAbsolutePath('export', exportRoute.path), resolveRouteLabel(exportRoute))
    );
  }
  configureChildren.push(buildFinanceAdvancedDomainMenuItem(icons.configure));

  return [
    {
      ...system,
      key: configure.navKey,
      label: configure.userFacingLabel,
      path: configure.defaultLanding,
      children: configureChildren,
      adminOnly: true,
    },
    {
      ...conscious,
      key: consoleLayer.navKey,
      label: consoleLayer.userFacingLabel,
      path: consoleLayer.defaultLanding,
      adminOnly: true,
    },
  ];
}
